import React, { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { IoIosArrowBack } from 'react-icons/io'
import { useSelector } from 'react-redux'
import PostModel from '../model/posts'
import { isMusicPost, isShortPost, leavePath } from '../utils/mediaRoute'
import { usesSound } from '../utils/sound'
import '../styles/soundPage.css'

const toPost = (row) => {
  if (!row || row.msg) return null
  const id = row.id || row.postid
  if (!id) return null
  return new PostModel(
    id,
    row.image || row.content,
    row.title,
    row.type,
    row.userid,
    row.tages,
    row.disc || row.discrption || row.description,
    row.time,
    row.thumbnail,
    row.vCount || row.views,
    row.comm || row.vcomments,
    row.pCount || row.vlikes,
    row.dCount || row.vdislikes,
    row.userImg,
    row.email,
    row.userLink
  )
}

const SoundPage = () => {
  const { soundId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const startItem = location.state && location.state.item
  const shorts = useSelector(state => state.PostReducer.followShort) || []
  const userPosts = useSelector(state => state.PostReducer.userPosts) || []
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let live = true
    const loadJson = (url) => Promise.race([
      fetch(url).then(res => (res.ok ? res.json() : [])).catch(() => []),
      new Promise((resolve) => setTimeout(() => resolve([]), 8000))
    ])
    const load = async () => {
      setLoading(true)
      const collected = []
      try {
        const [bySound, byId] = await Promise.all([
          loadJson(`/api/posts/sound/${encodeURIComponent(soundId)}`),
          loadJson(`/api/posts/${encodeURIComponent(soundId)}`)
        ])
        ;[bySound, byId].forEach((list) => {
          const items = Array.isArray(list) ? list : (list && list.id ? [list] : [])
          items.forEach((row) => {
            const post = toPost(row)
            if (post) collected.push(post)
          })
        })
      } catch (err) {}
      if (live) {
        setRows(collected)
        setLoading(false)
      }
    }
    load()
    return () => { live = false }
  }, [soundId])

  const videos = useMemo(() => {
    const seen = new Set()
    const out = []
    const add = (item, force) => {
      if (!item || !item.id || seen.has(String(item.id))) return
      if (isMusicPost(item)) return
      if (force) {
        seen.add(String(item.id))
        out.push(item)
        return
      }
      if (!isShortPost(item) || !usesSound(item, soundId)) return
      seen.add(String(item.id))
      out.push(item)
    }
    add(startItem, true)
    rows.forEach(item => add(item, false))
    ;(Array.isArray(shorts) ? shorts : []).forEach(item => add(item, false))
    ;(Array.isArray(userPosts) ? userPosts : []).forEach(item => add(item, false))
    return out
  }, [startItem, rows, shorts, userPosts, soundId])

  const cover = rows.find(row => String(row.id) === String(soundId)) || startItem || videos[0]
  const title = (cover && cover.title) || 'Original sound'

  const openVideo = (item) => {
    navigate(`/watch?v=${encodeURIComponent(item.id)}&sound=${encodeURIComponent(soundId)}`, {
      state: { item, soundFeed: videos, fromSound: soundId, from: `/sound/${encodeURIComponent(soundId)}` }
    })
  }

  const goBack = () => {
    const target = leavePath(location)
    const resumeId = startItem && startItem.id
    const next = target === `/sound/${soundId}` ? '/watch' : target
    if (next === '/watch' || String(next).startsWith('/watch')) {
      navigate('/watch', { replace: true, state: { resumeId } })
      return
    }
    navigate(next, { replace: true, state: resumeId ? { resumeId } : undefined })
  }

  return (
    <section className="soundPage">
      <Helmet>
        <title>{title} | Pinweelo</title>
        <meta name="theme-color" content="#000000" />
      </Helmet>
      <header className="soundTop">
        <button type="button" className="soundBack" onClick={goBack} aria-label="Go back">
          <IoIosArrowBack />
        </button>
        <h1>Videos with this sound</h1>
        <span className="soundTopSpace" aria-hidden="true" />
      </header>

      <div className="soundHero">
        {cover && cover.thumbnail ? (
          <img src={`/api/uploads/${cover.thumbnail}`} alt="" />
        ) : <div className="soundHeroEmpty" />}
        <div>
          <p>Original sound</p>
          <h2>{title}</h2>
          <small>{videos.length} {videos.length === 1 ? 'video' : 'videos'}</small>
        </div>
      </div>

      {loading && !videos.length ? (
        <p className="soundEmpty">Loading videos…</p>
      ) : !videos.length ? (
        <p className="soundEmpty">No videos use this sound yet. This clip will show here after it loads.</p>
      ) : (
        <div className="soundGrid">
          {videos.map(item => (
            <button
              key={item.id}
              type="button"
              className="soundCell"
              onClick={() => openVideo(item)}
            >
              <img src={`/api/uploads/${item.thumbnail || item.image}`} alt={item.title || 'video'} />
            </button>
          ))}
        </div>
      )}
    </section>
  )
}

export default SoundPage
